import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

type DocTab = 'overview' | 'api' | 'database' | 'auth' | 'roles' | 'logic' | 'architecture' | 'user-management' | 'demo';

interface ApiEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  auth: boolean;
  params?: string[];
  permissions?: string[];
}

const API_ENDPOINTS: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/api/auth/me',
    description: 'Get current authenticated user',
    auth: true,
  },
  {
    method: 'POST',
    path: '/api/demo-auth/login',
    description: 'Demo mode login',
    auth: false,
    params: ['role'],
  },
  {
    method: 'GET',
    path: '/api/requests',
    description: 'Get all requests for user\'s assigned branches',
    auth: true,
    params: ['status?', 'branchId?'],
    permissions: ['requests.view'],
  },
  {
    method: 'GET',
    path: '/api/requests/:id',
    description: 'Get single request with updates',
    auth: true,
    params: ['id'],
    permissions: ['requests.view'],
  },
  {
    method: 'POST',
    path: '/api/requests',
    description: 'Create new service request',
    auth: true,
    params: ['title', 'description', 'priority', 'location', 'branchId'],
    permissions: ['requests.create'],
  },
  {
    method: 'PUT',
    path: '/api/requests/:id',
    description: 'Update request details',
    auth: true,
    params: ['id'],
    permissions: ['requests.edit'],
  },
  {
    method: 'PUT',
    path: '/api/requests/:id/status',
    description: 'Update request status and track response/completion time',
    auth: true,
    params: ['id', 'status', 'note?'],
    permissions: ['requests.edit'],
  },
  {
    method: 'GET',
    path: '/api/requests/dashboard/stats',
    description: 'Get dashboard statistics',
    auth: true,
  },
  {
    method: 'GET',
    path: '/api/company',
    description: 'Get company info and branches',
    auth: true,
    permissions: ['company.view'],
  },
  {
    method: 'PUT',
    path: '/api/company',
    description: 'Update company info',
    auth: true,
    permissions: ['company.edit'],
  },
  {
    method: 'GET',
    path: '/api/branches',
    description: 'Get user\'s assigned branches',
    auth: true,
    permissions: ['branches.view'],
  },
  {
    method: 'POST',
    path: '/api/branches',
    description: 'Create new branch',
    auth: true,
    permissions: ['branches.create'],
  },
  {
    method: 'PUT',
    path: '/api/branches/:id',
    description: 'Update branch details',
    auth: true,
    permissions: ['branches.edit'],
  },
  {
    method: 'DELETE',
    path: '/api/branches/:id',
    description: 'Delete branch',
    auth: true,
    permissions: ['branches.delete'],
  },
  {
    method: 'POST',
    path: '/api/admin/invitations',
    description: 'Create user invitation',
    auth: true,
    permissions: ['invitations.create'],
  },
  {
    method: 'GET',
    path: '/api/admin/invitations',
    description: 'List company invitations',
    auth: true,
    permissions: ['invitations.view'],
  },
  {
    method: 'DELETE',
    path: '/api/admin/invitations/:code',
    description: 'Revoke user invitation',
    auth: true,
    permissions: ['invitations.revoke'],
  },
  {
    method: 'GET',
    path: '/api/admin/users',
    description: 'Get all company users',
    auth: true,
    permissions: ['users.view'],
  },
  {
    method: 'PUT',
    path: '/api/admin/users/:id',
    description: 'Update user profile',
    auth: true,
    params: ['id'],
    permissions: ['users.edit'],
  },
  {
    method: 'PUT',
    path: '/api/admin/users/:id/role',
    description: 'Change user role',
    auth: true,
    params: ['id', 'role'],
    permissions: ['users.changeRole'],
  },
  {
    method: 'PUT',
    path: '/api/admin/users/:id/lock',
    description: 'Lock/unlock user account',
    auth: true,
    params: ['id', 'isLocked'],
    permissions: ['users.lockUnlock'],
  },
  {
    method: 'DELETE',
    path: '/api/admin/users/:id',
    description: 'Delete user account - removes sessions (triggers logout), deletes user_branch associations, soft-deletes user',
    auth: true,
    params: ['id'],
    permissions: ['users.delete'],
  },
  {
    method: 'PUT',
    path: '/api/admin/users/:id/avatar',
    description: 'Upload user avatar',
    auth: true,
    permissions: ['users.edit'],
  },
  {
    method: 'GET',
    path: '/api/admin/users/avatar/:userId',
    description: 'Get signed URL for avatar',
    auth: true,
  },
  {
    method: 'GET',
    path: '/api/roles',
    description: 'Get all roles with permissions',
    auth: true,
    permissions: ['roles.view'],
  },
  {
    method: 'PUT',
    path: '/api/roles/:roleId',
    description: 'Update role permissions',
    auth: true,
    permissions: ['roles.edit'],
  },
  {
    method: 'POST',
    path: '/api/notifications/register',
    description: 'Register push token',
    auth: false,
  },
];

const TABLES = [
  {
    name: 'user',
    fields: [
      'id (PK)',
      'email (unique)',
      'name',
      'avatar (file path)',
      'role (company_owner, manager, technician, operator, app_owner)',
      'isDemo',
      'companyId (FK)',
      'onboardingStatus',
      'isLocked',
      'isDeleted',
      'createdAt, updatedAt',
    ],
  },
  {
    name: 'company',
    fields: [
      'id (PK)',
      'name',
      'email (unique)',
      'phone, address',
      'subscriptionStatus',
      'isDemo',
      'createdAt, updatedAt',
    ],
  },
  {
    name: 'branch',
    fields: [
      'id (PK)',
      'name',
      'location',
      'companyId (FK)',
      'createdAt, updatedAt',
    ],
  },
  {
    name: 'service_request',
    fields: [
      'id (PK)',
      'ticketNumber (unique, format: WM000001)',
      'title, description',
      'status (open, in_progress, pending, completed)',
      'priority (low, medium, high, urgent)',
      'category, location',
      'branchId (FK)',
      'technicianId (FK, nullable)',
      'responseTime, completionTime (minutes)',
      'leakageType, materialUsed',
      'completedAt',
      'createdAt, updatedAt',
    ],
  },
  {
    name: 'request_update',
    fields: [
      'id (PK)',
      'requestId (FK)',
      'note',
      'status',
      'createdAt',
    ],
  },
  {
    name: 'user_branch',
    fields: [
      'id (PK)',
      'userId (FK)',
      'branchId (FK)',
      'createdAt',
      'Unique: (userId, branchId)',
    ],
  },
  {
    name: 'user_invitation',
    fields: [
      'id (PK)',
      'code (6-char, unique)',
      'email',
      'companyId (FK)',
      'createdBy (FK)',
      'role',
      'branches (JSON array)',
      'status (pending, accepted, rejected, expired)',
      'expiresAt, acceptedAt, rejectedAt',
      'createdAt, updatedAt',
    ],
  },
  {
    name: 'push_token',
    fields: [
      'id (PK)',
      'userId',
      'token (unique, Expo token)',
      'deviceType (ios, android)',
      'deviceName',
      'isDemo',
      'isActive',
      'createdAt, updatedAt',
    ],
  },
  {
    name: 'session',
    fields: [
      'id (PK)',
      'userId (FK)',
      'token (unique)',
      'expiresAt',
      'ipAddress, userAgent',
      'createdAt, updatedAt',
    ],
  },
];

const ROLES = [
  {
    role: 'company_owner',
    description: 'Full control of company - All permissions granted',
    permissions: {
      users: ['view', 'create', 'edit', 'delete', 'changeRole', 'lockUnlock'],
      company: ['view', 'edit'],
      branches: ['view', 'create', 'edit', 'delete'],
      roles: ['view', 'edit'],
      requests: ['view', 'create', 'edit', 'delete', 'assign'],
      reports: ['view', 'export'],
      billing: ['view', 'edit'],
      setup: ['view', 'edit'],
      invitations: ['view', 'create', 'revoke'],
    },
  },
  {
    role: 'manager',
    description: 'Operational oversight - Limited access',
    permissions: {
      users: ['view'],
      company: ['view'],
      branches: ['view'],
      roles: [],
      requests: ['view', 'create', 'edit', 'assign'],
      reports: ['view', 'export'],
      billing: [],
      setup: [],
      invitations: ['view'],
    },
  },
  {
    role: 'technician',
    description: 'Field service handler - View and edit requests only',
    permissions: {
      users: [],
      company: ['view'],
      branches: ['view'],
      roles: [],
      requests: ['view', 'create', 'edit'],
      reports: [],
      billing: [],
      setup: [],
      invitations: [],
    },
  },
  {
    role: 'operator',
    description: 'Request creator - View requests only',
    permissions: {
      users: [],
      company: [],
      branches: [],
      roles: [],
      requests: ['view'],
      reports: [],
      billing: [],
      setup: [],
      invitations: [],
    },
  },
];

interface ExpandableItemProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

function ExpandableItem({ title, children, defaultExpanded = false }: ExpandableItemProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
      >
        <span className="font-semibold text-gray-900">{title}</span>
        <ChevronDown
          size={18}
          className={`transition-transform flex-shrink-0 ml-2 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded && <div className="px-4 py-3 bg-gray-50">{children}</div>}
    </div>
  );
}

function ApiEndpointItem({ endpoint }: { endpoint: ApiEndpoint }) {
  const methodColors: Record<string, string> = {
    GET: '#10b981',
    POST: '#3b82f6',
    PUT: '#f59e0b',
    DELETE: '#ef4444',
  };

  return (
    <div className="mb-4 bg-gray-50 rounded-lg p-3">
      <div className="flex items-center mb-2">
        <span
          style={{ backgroundColor: methodColors[endpoint.method] }}
          className="px-2 py-1 rounded text-white text-xs font-bold"
        >
          {endpoint.method}
        </span>
        <span className="ml-2 text-sm font-mono text-gray-900 flex-1">
          {endpoint.path}
        </span>
      </div>
      <p className="text-sm text-gray-700 mb-2">{endpoint.description}</p>
      {endpoint.params && (
        <p className="text-xs text-gray-600 mb-1">
          <span className="font-semibold">Parameters:</span> {endpoint.params.join(', ')}
        </p>
      )}
      {endpoint.permissions && (
        <p className="text-xs text-green-700 font-semibold mb-1">
          ✓ Permission Required: {endpoint.permissions.join(' OR ')}
        </p>
      )}
      <p className="text-xs text-gray-600">
        <span className="font-semibold">Auth:</span> {endpoint.auth ? 'Bearer Token' : 'None'}
      </p>
    </div>
  );
}

export default function Developer() {
  const params = useParams<{ tab?: string }>();
  const { user } = useAuth();

  // Only allow app_owner and company_owner to access developer docs
  const isAuthorized = user?.role === 'app_owner' || user?.role === 'company_owner';

  // This conditional return is safe - it happens during render phase, after all hooks
  if (!isAuthorized) {
    return (
      <div className="p-8">
        <div className="bg-red-50 rounded-lg p-6 border border-red-200">
          <h2 className="text-2xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-800">
            Developer documentation is only accessible to app owners and company owners.
          </p>
        </div>
      </div>
    );
  }

  const activeTab = (params.tab as DocTab) || 'overview';

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Developer Documentation</h2>

            <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200">
              <p className="text-sm text-blue-900">
                <span className="font-bold">Water Management Service App</span>
                {'\n\n'}
                A comprehensive field service management system for water utilities.
                Full-stack TypeScript app with React Native frontend, Hono backend,
                Prisma ORM, and Supabase PostgreSQL database.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Stack Overview</h3>
              <p className="text-sm text-gray-700 space-y-1 whitespace-pre-line">
                <span className="font-semibold">Frontend:</span> Expo SDK 53, React Native, Zustand, Nativewind
                {'\n'}
                <span className="font-semibold">Backend:</span> Hono, Prisma ORM, Supabase Auth
                {'\n'}
                <span className="font-semibold">Database:</span> Supabase PostgreSQL
                {'\n'}
                <span className="font-semibold">Storage:</span> Supabase Storage
                {'\n'}
                <span className="font-semibold">Notifications:</span> Expo Push Notifications
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Key Features</h3>
              <p className="text-sm text-gray-700 space-y-1 whitespace-pre-line">
                • Service request management with ticket tracking
                {'\n'}• Multi-branch organization structure
                {'\n'}• Role-based access control (5 roles)
                {'\n'}• Real-time push notifications
                {'\n'}• Avatar storage with signed URLs
                {'\n'}• Email-based user invitations
                {'\n'}• Comprehensive audit trails
              </p>
            </div>
          </div>
        );

      case 'api':
        return (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">API Endpoints</h2>

            <ExpandableItem title="Authentication" defaultExpanded={true}>
              <div className="space-y-3">
                <ApiEndpointItem
                  endpoint={{
                    method: 'GET',
                    path: '/api/auth/me',
                    description: 'Get current authenticated user',
                    auth: true,
                  }}
                />
                <ApiEndpointItem
                  endpoint={{
                    method: 'POST',
                    path: '/api/demo-auth/login',
                    description: 'Demo mode login',
                    auth: false,
                    params: ['role'],
                  }}
                />
              </div>
            </ExpandableItem>

            <ExpandableItem title="Service Requests">
              {API_ENDPOINTS.filter(
                (e) =>
                  e.path.includes('/requests') &&
                  !e.path.includes('stats')
              ).map((endpoint, idx) => (
                <ApiEndpointItem key={idx} endpoint={endpoint} />
              ))}
            </ExpandableItem>

            <ExpandableItem title="Dashboard & Analytics">
              {API_ENDPOINTS.filter((e) =>
                e.path.includes('stats')
              ).map((endpoint, idx) => (
                <ApiEndpointItem key={idx} endpoint={endpoint} />
              ))}
            </ExpandableItem>

            <ExpandableItem title="Company & Branches">
              {API_ENDPOINTS.filter(
                (e) =>
                  (e.path.includes('/company') ||
                    e.path.includes('/branches')) &&
                  !e.path.includes('unassigned')
              ).map((endpoint, idx) => (
                <ApiEndpointItem key={idx} endpoint={endpoint} />
              ))}
            </ExpandableItem>

            <ExpandableItem title="User Management">
              {API_ENDPOINTS.filter((e) =>
                e.path.includes('/admin')
              ).map((endpoint, idx) => (
                <ApiEndpointItem key={idx} endpoint={endpoint} />
              ))}
            </ExpandableItem>

            <ExpandableItem title="Invitations">
              {API_ENDPOINTS.filter((e) =>
                e.path.includes('invitation')
              ).map((endpoint, idx) => (
                <ApiEndpointItem key={idx} endpoint={endpoint} />
              ))}
            </ExpandableItem>

            <ExpandableItem title="Notifications">
              {API_ENDPOINTS.filter((e) =>
                e.path.includes('notification')
              ).map((endpoint, idx) => (
                <ApiEndpointItem key={idx} endpoint={endpoint} />
              ))}
            </ExpandableItem>

            <div className="mt-6 bg-yellow-50 rounded-lg p-4 mb-6 border border-yellow-200">
              <p className="text-sm text-yellow-900">
                <span className="font-bold">Authentication Header</span>
                {'\n\n'}
                All protected endpoints require:
                {'\n'}
                <span className="font-mono">Authorization: Bearer &quot;{'{token}'}&quot;</span>
                {'\n\n'}
                Demo tokens use prefix: &quot;demo-&quot;
              </p>
            </div>
          </div>
        );

      case 'database':
        return (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Database Schema</h2>

            <div className="bg-indigo-50 rounded-lg p-4 mb-4 border border-indigo-200">
              <p className="text-sm text-indigo-900">
                <span className="font-bold">PostgreSQL via Supabase</span>
                {'\n'}
                All timestamps (createdAt, updatedAt) are automatically managed.
                Relationships use foreign keys with automatic cascade rules.
              </p>
            </div>

            {TABLES.map((table, idx) => (
              <ExpandableItem key={idx} title={table.name}>
                <div className="space-y-2">
                  {table.fields.map((field, fidx) => (
                    <p
                      key={fidx}
                      className="text-sm text-gray-700 font-mono"
                    >
                      • {field}
                    </p>
                  ))}
                </div>
              </ExpandableItem>
            ))}

            <div className="mt-6 bg-green-50 rounded-lg p-4 mb-6 border border-green-200">
              <p className="text-sm text-green-900">
                <span className="font-bold">Key Relationships</span>
                {'\n\n'}
                • user → company (many-to-one)
                {'\n'}
                • user → branch (many-to-many via user_branch)
                {'\n'}
                • service_request → branch (many-to-one)
                {'\n'}
                • service_request → user (technician assignment)
                {'\n'}
                • request_update → service_request (one-to-many)
              </p>
            </div>
          </div>
        );

      case 'auth':
        return (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication & Authorization</h2>

            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Authentication Methods</h3>
              <p className="text-sm text-gray-700 mb-4 space-y-2 whitespace-pre-line">
                <span className="font-semibold">OAuth (Primary)</span>
                {'\n'}Email/password, Google, GitHub via Better Auth
                {'\n\n'}
                <span className="font-semibold">Demo Mode</span>
                {'\n'}Token prefix: &quot;demo-&quot;
                {'\n'}Stored in session table
                {'\n'}
                30-day expiration
              </p>
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Token Validation Flow</h3>
              <p className="text-sm text-gray-700 mb-4 space-y-1 whitespace-pre-line">
                1. Extract token from Authorization header
                {'\n'}
                2. Check if demo token (prefix &quot;demo-&quot;)
                {'\n'}
                3. If demo: Validate in session table
                {'\n'}
                4. If OAuth: Decode JWT and verify expiry
                {'\n'}
                5. Fallback: Use Supabase getUser() verification
                {'\n'}
                6. Auto-create user on first OAuth login
              </p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4 mb-4 border border-purple-200">
              <p className="text-sm text-purple-900">
                <span className="font-bold">Context Variables in Routes</span>
                {'\n\n'}
                <span className="font-mono">{`c.get("user")`}</span> - AuthUser object (id, email, name)
                {'\n'}
                <span className="font-mono">{`c.get("session")`}</span> - Session object (id, token, expiresAt)
                {'\n'}
                <span className="font-mono">{`c.get("userId")`}</span> - String user ID
              </p>
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Branch Access Control</h3>
              <p className="text-sm text-gray-700 mb-4">
                Users are assigned to branches via user_branch table. All operations verify branch access:
                - Query filters automatically apply user&apos;s branches - company_owner gets access to all company branches -
                Service request access requires branch assignment
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Session Management</h3>
              <p className="text-sm text-gray-700 space-y-1 whitespace-pre-line">
                • Sessions stored in database with expiry
                {'\n'}• 30-day default session lifetime
                {'\n'}• IP address and user agent tracking
                {'\n'}
                • Automatic cleanup of expired sessions
              </p>
            </div>
          </div>
        );

      case 'roles':
        return (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Roles & Permissions</h2>

            {ROLES.map((roleData, idx) => (
              <ExpandableItem
                key={idx}
                title={`${roleData.role.replace('_', ' ').toUpperCase()} - ${roleData.description}`}
                defaultExpanded={idx === 0}
              >
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-100 border-b border-gray-300">
                          <th className="text-left px-3 py-2 font-semibold text-gray-900">Resource</th>
                          <th className="text-left px-3 py-2 font-semibold text-gray-900">Permissions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(roleData.permissions).map(([resource, actions]) => (
                          <tr key={resource} className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-700">{resource}</td>
                            <td className="px-3 py-2 text-gray-600">
                              {actions.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {actions.map((action) => (
                                    <span key={action} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                                      {action}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-400 italic">No access</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </ExpandableItem>
            ))}

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-bold text-blue-900 mb-3">Permission Format</p>
              <p className="text-xs text-blue-800 font-mono whitespace-pre-line mb-3">
                Permissions are checked as: resource.action{'\n'}
                Example: requests.view, requests.create, users.edit
              </p>
              <p className="text-sm font-bold text-blue-900 mb-2">Default Roles Created for New Companies:</p>
              <ul className="text-xs text-blue-800 space-y-1 ml-4">
                <li>• <strong>company_owner</strong> - Full access to all resources</li>
                <li>• <strong>manager</strong> - Limited access: view users, branches, requests; can create/edit requests; view reports</li>
                <li>• <strong>technician</strong> - Field work: view/create/edit requests; view company and branches only</li>
                <li>• <strong>operator</strong> - Minimal: view requests only</li>
              </ul>
            </div>
          </div>
        );

      case 'architecture':
        return (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">App Architecture</h2>

            {/* Web App Development */}
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-bold text-blue-900 mb-4">Web App Development Workflow (React + Vite)</h3>

              <div className="mb-4">
                <p className="text-sm font-semibold text-blue-900 mb-2">Development → Production Architecture:</p>
                <p className="text-sm text-blue-800 space-y-1 whitespace-pre-line">
                  • Development: Vibecode (port 3002) with 100ms hot reload
                  {'\n'}• Production: Fly.io (auto-builds and deploys on git push)
                  {'\n'}• Mobile: Expo (separate app, handles app store distribution)
                  {'\n'}• No redundant local production builds
                </p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-semibold text-blue-900 mb-2">Development Workflow:</p>
                <p className="text-sm text-blue-800 space-y-1 whitespace-pre-line">
                  1. Edit files in /web/src/ (components, pages, hooks)
                  {'\n'}2. Save the file (Ctrl+S)
                  {'\n'}3. See change instantly on port 3002 (100ms hot reload)
                  {'\n'}4. Test the change
                  {'\n'}5. When ready: git push origin main
                  {'\n'}6. Fly.io auto-builds: bun run build
                  {'\n'}7. Fly.io auto-deploys to aquaflow.fly.dev
                  {'\n'}8. Users see latest version (cache busting prevents stale content)
                </p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-semibold text-blue-900 mb-2">Dev Server Management:</p>
                <p className="text-sm text-blue-800 space-y-1 whitespace-pre-line">
                  Start: /web/manage-dev.sh start
                  {'\n'}Status: /web/manage-dev.sh status
                  {'\n'}Logs: /web/manage-dev.sh logs
                  {'\n'}Restart: /web/manage-dev.sh restart
                  {'\n'}Stop: /web/manage-dev.sh stop
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-blue-900 mb-2">Important Notes:</p>
                <p className="text-sm text-blue-800 space-y-1">
                  ✓ Dev is for testing only - Fly.io is real production
                  {'\n'}✓ Do NOT manually run bun run build locally
                  {'\n'}✓ Do NOT worry about dist/ folder - Fly.io handles it
                  {'\n'}✓ No auto-sync script needed - Fly.io builds on deployment
                  {'\n'}✓ One source of truth - Code in git is what matters
                </p>
              </div>
            </div>

            {/* Cache Busting */}
            <div className="mb-8 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h3 className="text-lg font-bold text-purple-900 mb-4">Automatic Cache Busting (Production Deployment)</h3>

              <div className="mb-4">
                <p className="text-sm font-semibold text-purple-900 mb-2">Problem Solved:</p>
                <p className="text-sm text-purple-800">
                  Users were seeing old cached content even after code updates. Now automatically resolved via cache control headers.
                </p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-semibold text-purple-900 mb-2">How It Works:</p>
                <p className="text-sm text-purple-800 space-y-1 whitespace-pre-line">
                  1. index.html is never cached (cache-control meta tags + HTTP headers)
                  {'\n'}2. Assets use hash-based filenames (index-Caj36vJo.js)
                  {'\n'}3. Code changes = new hash = new filename
                  {'\n'}4. Old cached assets never used (different names)
                  {'\n'}5. Users always see latest version on page load
                </p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-semibold text-purple-900 mb-2">Deployment Flow:</p>
                <p className="text-sm text-purple-800 space-y-1 whitespace-pre-line">
                  git push origin main
                  {'\n'}  ↓
                  {'\n'}Fly.io auto-builds and deploys
                  {'\n'}  ↓
                  {'\n'}New assets get new hash-based filenames
                  {'\n'}  ↓
                  {'\n'}Browser requests fresh index.html (never cached)
                  {'\n'}  ↓
                  {'\n'}Browser sees new asset hashes
                  {'\n'}  ↓
                  {'\n'}Users automatically see latest version
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-purple-900 mb-2">Key Point:</p>
                <p className="text-sm text-purple-800">
                  ✓ Users do NOT need manual refresh (Ctrl+Shift+R) to see updates
                  {'\n'}✓ Completely automatic and transparent
                  {'\n'}✓ Works across all browsers and CDNs
                </p>
              </div>
            </div>

            {/* Web App Structure */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Web App Directory Structure</h3>
              <p className="text-sm text-gray-700 mb-4 space-y-1 whitespace-pre-line">
                <span className="font-semibold">/web/src/components/</span>
                {'\n'}Reusable React components (Layout, DeveloperMenu, etc)
                {'\n'}
                <span className="font-semibold">/web/src/pages/</span>
                {'\n'}Page components for routing (Dashboard, Login, etc)
                {'\n'}
                <span className="font-semibold">/web/src/hooks/</span>
                {'\n'}Custom React hooks (useAuth, useSidebarRefresh, etc)
                {'\n'}
                <span className="font-semibold">/web/src/contexts/</span>
                {'\n'}React Context for global state (AuthContext, PermissionsContext)
                {'\n'}
                <span className="font-semibold">/web/src/lib/</span>
                {'\n'}Utilities: api.ts (client), avatarUtils.ts, utils.ts
                {'\n'}
                <span className="font-semibold">/web/src/constants/</span>
                {'\n'}App-wide constants and configuration
              </p>
            </div>

            {/* Mobile App Structure */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Mobile App Structure (React Native)</h3>
              <p className="text-sm text-gray-700 mb-4 space-y-1 whitespace-pre-line">
                <span className="font-semibold">src/screens/</span>
                {'\n'}Dashboard, Requests, Profile, Settings, CompanyDetails, BranchesScreen
                {'\n'}
                <span className="font-semibold">src/navigation/</span>
                {'\n'}RootNavigator - Route registration and stack navigation
                {'\n'}
                <span className="font-semibold">src/lib/</span>
                {'\n'}useSession - Auth hook, API client, utility functions
                {'\n'}
                <span className="font-semibold">src/store/</span>
                {'\n'}Zustand store for global state management
              </p>
            </div>

            {/* Backend Structure */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Backend Structure (Hono)</h3>
              <p className="text-sm text-gray-700 mb-4 space-y-1 whitespace-pre-line">
                <span className="font-semibold">backend/src/routes/</span>
                {'\n'}Modular route handlers (auth, requests, company, admin, etc)
                {'\n'}
                <span className="font-semibold">backend/src/middleware/</span>
                {'\n'}CORS, logging, authentication, static file serving
                {'\n'}
                <span className="font-semibold">shared/contracts.ts</span>
                {'\n'}Zod schemas for request/response validation
              </p>
            </div>

            {/* Data Flow */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Data Flow</h3>
              <p className="text-sm text-gray-700 mb-4 space-y-1 whitespace-pre-line">
                1. Frontend calls API client (web/src/lib/api.ts or src/lib/api.ts)
                {'\n'}
                2. Backend validates request with Zod schema
                {'\n'}
                3. Database query via Prisma ORM
                {'\n'}
                4. Push notifications triggered on state changes
                {'\n'}
                5. Response sent back to frontend with typed data
              </p>
            </div>

            {/* State Management */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">State Management</h3>
              <p className="text-sm text-gray-700 mb-4 space-y-1 whitespace-pre-line">
                <span className="font-semibold">Web App:</span> React Context (AuthContext, PermissionsContext)
                {'\n'}
                <span className="font-semibold">Mobile App:</span> Zustand with selector pattern for minimal re-renders
                {'\n'}
                <span className="font-semibold">Backend:</span> Immutable database records with audit trails
              </p>
            </div>

            {/* Real-Time Features */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Real-Time Features</h3>
              <p className="text-sm text-gray-700 space-y-1 whitespace-pre-line">
                • Push notifications via Expo (configurable targeting)
                {'\n'}• Avatar uploads with signed URLs (Supabase Storage)
                {'\n'}• Web app hot reload (100ms change detection)
                {'\n'}• Mobile Fast Refresh (instant app updates)
                {'\n'}• Service request audit trails tracked in request_update table
              </p>
            </div>
          </div>
        );

      case 'logic':
        return (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Feature & Flow Logic</h2>

            <p className="text-sm text-gray-600 mb-6">
              Detailed step-by-step documentation of major system workflows. Use as reference for implementing features, creating help documentation, and planning enhancements.
            </p>

            <ExpandableItem title="1. User Deletion Flow" defaultExpanded={true}>
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Overview:</p>
                  <p>Complete account removal with automatic session termination and data cleanup.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Trigger:</p>
                  <p>Manager/Company Owner deletes user via Admin Panel → DELETE /api/admin/users/:id</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Process:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-xs">
                    <li>Validate permission (users.delete)</li>
                    <li>Delete user_invitation records</li>
                    <li>Delete user_branch associations (cascade)</li>
                    <li>Delete account records (cascade)</li>
                    <li>Delete session records (cascade)</li>
                    <li>Hard-delete user record</li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold mb-1">Result:</p>
                  <p>User and all related data removed. No re-login possible. Active sessions auto-logout on next API call.</p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="2. Account Locking Flow">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Overview:</p>
                  <p>Reversible account suspension without data loss.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Lock:</p>
                  <p>Admin clicks lock icon → PUT /api/admin/users/:id/lock (isLocked: true)</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Locked User Behavior:</p>
                  <ul className="list-disc ml-5 text-xs space-y-1">
                    <li>Login returns 403 with isLocked: true</li>
                    <li>Redirected to /locked (web) or LockedAccountScreen (mobile)</li>
                    <li>Shows lock icon and message</li>
                    <li>Monitors unlock state every 10 seconds</li>
                    <li>Auto-redirects when unlocked</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Unlock:</p>
                  <p>Admin clicks unlock → isLocked becomes false. User can access app on next login.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Key Difference from Deletion:</p>
                  <p>Data preserved, reversible, no cascade deletes.</p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="3. Session Cleanup Job">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Overview:</p>
                  <p>Automatic background cleanup of expired and orphaned sessions.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">What Gets Cleaned:</p>
                  <ul className="list-disc ml-5 text-xs space-y-1">
                    <li>Expired sessions (expiresAt &lt; NOW())</li>
                    <li>Orphaned sessions (userId = null)</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Schedule:</p>
                  <ul className="list-disc ml-5 text-xs space-y-1">
                    <li>On server startup: 5 seconds after backend starts</li>
                    <li>Hourly: Scheduled job via setInterval</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Why It Matters:</p>
                  <p>Keeps database lean, prevents unbounded table growth, no user impact.</p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="4. Auto-Logout for Deleted Users">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Scenario:</p>
                  <p>User logged in, gets deleted, tries to access app.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Web Flow:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-xs">
                    <li>API call made with old JWT</li>
                    <li>Backend: Session lookup fails → 401</li>
                    <li>Frontend: Clear API token, sign out from Supabase</li>
                    <li>Reload page in 100ms</li>
                    <li>LoginScreen renders with no session</li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold mb-1">Mobile Flow:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-xs">
                    <li>API call → Backend returns 401</li>
                    <li>Frontend: Clear Zustand store, navigate to Login</li>
                    <li>Show &quot;You&apos;ve been logged out&quot; message</li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold mb-1">Why 100ms Delay:</p>
                  <p>Allows Supabase signout to complete before page reload.</p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="5. Demo Login System">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Overview:</p>
                  <p>Test app with 4 pre-configured roles without OAuth.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Flow:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-xs">
                    <li>User clicks &quot;Demo Login&quot; button</li>
                    <li>Choose role: Company Owner, Manager, Technician, Operator</li>
                    <li>Frontend loads bundled demo data instantly</li>
                    <li>Backend creates demo session (isDemo: true)</li>
                    <li>User can access app with hardcoded demo data</li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold mb-1">Exit:</p>
                  <p>Click &quot;Exit&quot; → Clear store, logout, return to LoginScreen. Data not saved.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Benefits:</p>
                  <p>No user account created, no persistent data, works offline.</p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="6. OAuth Onboarding">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Step 1:</p>
                  <p>User signs in with Google/Apple → Account record created</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Step 2:</p>
                  <p>Choose company (create new or join existing)</p>
                  <ul className="list-disc ml-5 text-xs space-y-1 mt-1">
                    <li>Create: Fill company details → Assigned as company_owner</li>
                    <li>Join: Enter 6-digit invite code → Assigned role from invitation</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Step 3:</p>
                  <p>Branch setup (add 1+ branches before accessing app)</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Step 4:</p>
                  <p>Welcome page → Access main app</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Database Created:</p>
                  <p>Account, Session, Company, UserCompany, UserBranch, UserRole records.</p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="7. Sidebar Refresh System">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Overview:</p>
                  <p>Sidebar updates instantly when user profile changes, no page reload.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Architecture:</p>
                  <ul className="list-disc ml-5 text-xs space-y-1">
                    <li>SidebarRefreshContext: Global provider</li>
                    <li>useSidebarRefresh: Fetches fresh user data</li>
                    <li>useSidebarRefreshTrigger: Allows any component to trigger refresh</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">When Triggered:</p>
                  <ul className="list-disc ml-5 text-xs space-y-1">
                    <li>After avatar upload → Avatar appears in sidebar immediately</li>
                    <li>After profile changes → Name/email update instantly</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Benefits:</p>
                  <p>No polling, no page reload, triggered only when needed.</p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="8. Avatar Upload & Display">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Upload:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-xs">
                    <li>User selects image → PUT /api/admin/users/:id/avatar</li>
                    <li>Backend validates (JPEG/PNG, max 5MB)</li>
                    <li>Upload to Supabase Storage (avatars/&#123;userId&#125;/&#123;randomUUID&#125;.jpg)</li>
                    <li>Auto-delete old avatar file</li>
                    <li>Store only file path in database</li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold mb-1">Display:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-xs">
                    <li>Component calls getAvatarUrl(&#123;userId&#125;)</li>
                    <li>Check cache (instant if cached)</li>
                    <li>If not cached: GET /api/admin/users/avatar/:userId</li>
                    <li>Backend generates signed URL (1-hour expiry)</li>
                    <li>Cache in memory, use in img src</li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold mb-1">Why Server Signed URLs:</p>
                  <p>Prevents direct S3 access, no credentials exposed, controlled expiry.</p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="9. Role-Based Access Control">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Roles:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-xs">
                    <li>app_owner - Super admin, full access</li>
                    <li>company_owner - Company admin, manage company/users/branches</li>
                    <li>manager - Team lead, manage technicians, view requests</li>
                    <li>technician - Field worker, view/update own requests</li>
                    <li>operator - Junior tech, limited view only</li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold mb-1">Permission Format:</p>
                  <p>resource.action (users.view, users.edit, requests.create)</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Frontend Check:</p>
                  <p>Hide/disable UI if permission not in list</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Backend Check:</p>
                  <p>Return 403 if permission denied</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Storage:</p>
                  <p>Role, Permission, RolePermission tables in database.</p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="10. Company Owner Visibility Control">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Overview:</p>
                  <p>Company owner users are hidden from non-owner members. Only company owners can see company owner users in the team members list.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Why This Matters:</p>
                  <p>Ensures role hierarchy separation. Managers, technicians, and operators don&apos;t see who the company owner is, maintaining admin privacy.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Implementation:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-xs">
                    <li>Frontend fetches all users from GET /api/admin/users endpoint</li>
                    <li>Client-side filter applied: Show all users if current user is company_owner</li>
                    <li>If current user is NOT company_owner: Filter out users with role = &quot;company_owner&quot;</li>
                    <li>Invitations list: Not filtered (invitations don&apos;t have role visibility restrictions)</li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold mb-1">Code Location:</p>
                  <p>web/src/pages/SettingsUsers.tsx, allItems filtering logic:</p>
                  <p className="font-mono bg-gray-100 rounded px-2 py-1 mt-1 text-xs">
                    users.filter(u =&gt; !u.isDeleted &amp;&amp; (currentUser?.role === &apos;company_owner&apos; || u.role !== &apos;company_owner&apos;))
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">User Experience:</p>
                  <ul className="list-disc ml-5 text-xs space-y-1">
                    <li>Company Owner: Sees all team members including other company owners</li>
                    <li>Manager: Sees all team members except company owners</li>
                    <li>Technician: Sees all team members except company owners</li>
                    <li>Operator: Sees all team members except company owners</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Note:</p>
                  <p>This is a frontend-only visibility control. Backend /api/admin/users endpoint still returns all users - the filtering happens on the client side before display.</p>
                </div>
              </div>
            </ExpandableItem>

            <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-blue-900">
                <span className="font-bold">Using This Documentation</span>
                {'\n\n'}
                <span className="font-semibold">For Development:</span> Reference when implementing similar features or fixing bugs
                {'\n'}
                <span className="font-semibold">For User Support:</span> Transform these flows into help center articles
                {'\n'}
                <span className="font-semibold">For Planning:</span> Understand existing logic before planning enhancements
              </p>
            </div>
          </div>
        );

      case 'user-management':
        return (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">User Management System</h2>

            <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
              <p className="text-sm text-blue-900">
                <span className="font-bold">Comprehensive User Lifecycle Management</span>
                {'\n\n'}
                Complete system for managing company users including account creation, role assignment,
                profile updates, account locking, and deletion with automatic session cleanup.
              </p>
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-3">User Deletion with Session Cleanup</h3>
            <ExpandableItem title="DELETE /api/admin/users/:id - Complete Account Removal" defaultExpanded={true}>
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Overview:</p>
                  <p>Deletes a user account and removes all associated sessions, triggering automatic logout across all devices.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Deletion Process (Sequential):</p>
                  <ol className="list-decimal ml-5 space-y-1">
                    <li><span className="font-mono">DELETE FROM session WHERE userId = ?</span> - Removes all active sessions</li>
                    <li><span className="font-mono">DELETE FROM user_branch WHERE userId = ?</span> - Removes branch access associations</li>
                    <li><span className="font-mono">UPDATE user SET isDeleted=true, deletedAt=NOW()</span> - Soft-deletes user record</li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold mb-1">Permission Required:</p>
                  <p className="font-mono bg-gray-100 rounded px-2 py-1">users.delete</p>
                  <p className="text-xs text-gray-600 mt-1">Only company_owner and manager roles have this permission</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">User Logout Trigger:</p>
                  <p>When user sessions are deleted from the database, the app automatically detects missing session token and logs the user out.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Data Preservation:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><span className="font-semibold">Soft Delete:</span> User record retained with isDeleted=true flag</li>
                    <li><span className="font-semibold">Audit Trail:</span> deletedAt timestamp recorded for compliance</li>
                    <li><span className="font-semibold">Data Integrity:</span> Associated records in service_request, request_update preserved</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Error Handling:</p>
                  <p>If session or user_branch deletion fails, the process continues with soft-delete. Errors are logged but don&apos;t block account removal.</p>
                </div>

                <div className="bg-yellow-50 rounded p-2 border border-yellow-200">
                  <p className="text-xs text-yellow-900">
                    <span className="font-semibold">Note:</span> Branch records remain intact. Only user_branch associations are deleted.
                  </p>
                </div>
              </div>
            </ExpandableItem>

            <h3 className="text-lg font-bold text-gray-900 mb-3 mt-6">Other User Management Operations</h3>

            <ExpandableItem title="GET /api/admin/users - List All Company Users">
              <div className="space-y-2 text-sm text-gray-700">
                <p><span className="font-semibold">Returns:</span> Active users + pending invitations in company</p>
                <p><span className="font-semibold">Permission:</span> users.view (all roles except operator)</p>
                <p><span className="font-semibold">Response Fields:</span></p>
                <ul className="list-disc ml-5 text-xs space-y-1">
                  <li>id, email, name, role, avatar</li>
                  <li>isLocked, isDeleted, lockedAt, deletedAt</li>
                  <li>createdAt, updatedAt timestamps</li>
                </ul>
              </div>
            </ExpandableItem>

            <ExpandableItem title="PUT /api/admin/users/:id - Update User Profile">
              <div className="space-y-2 text-sm text-gray-700">
                <p><span className="font-semibold">Allows:</span> Update name or role</p>
                <p><span className="font-semibold">Permission:</span> users.edit (for others), self-edit allowed</p>
                <p><span className="font-semibold">Restrictions:</span></p>
                <ul className="list-disc ml-5 text-xs space-y-1">
                  <li>Cannot change own role (role change requires users.changeRole)</li>
                  <li>Cannot change company_owner role</li>
                  <li>Role change requires users.changeRole permission</li>
                </ul>
              </div>
            </ExpandableItem>

            <ExpandableItem title="PUT /api/admin/users/:id/lock - Lock/Unlock Account">
              <div className="space-y-2 text-sm text-gray-700">
                <p><span className="font-semibold">Purpose:</span> Temporarily disable user account without deletion</p>
                <p><span className="font-semibold">Permission:</span> users.lockUnlock</p>
                <p><span className="font-semibold">Effects:</span></p>
                <ul className="list-disc ml-5 text-xs space-y-1">
                  <li>Sets isLocked flag and lockedAt timestamp</li>
                  <li>User remains in system but cannot login</li>
                  <li>Account can be unlocked later (non-destructive)</li>
                </ul>
              </div>
            </ExpandableItem>

            <ExpandableItem title="Avatar Management - Upload & Retrieve">
              <div className="space-y-2 text-sm text-gray-700">
                <p><span className="font-semibold">PUT /api/admin/users/:id/avatar</span></p>
                <ul className="list-disc ml-5 text-xs space-y-1">
                  <li>Upload to Supabase Storage bucket &quot;AquaFlow&quot;</li>
                  <li>Validates: JPEG/PNG only, 5MB max size</li>
                  <li>Auto-deletes old avatar file when new one uploaded</li>
                  <li>Path format: avatars/{'{userId}'}/{'{randomUUID}'}.jpg</li>
                </ul>

                <p className="mt-2"><span className="font-semibold">GET /api/admin/users/avatar/:userId</span></p>
                <ul className="list-disc ml-5 text-xs space-y-1">
                  <li>Returns signed URL (1-hour expiry)</li>
                  <li>Backend handles URL generation for security</li>
                  <li>Prevents direct Supabase Storage access</li>
                </ul>
              </div>
            </ExpandableItem>

            <div className="mt-6 bg-purple-50 rounded-lg p-4 border border-purple-200">
              <p className="text-sm text-purple-900">
                <span className="font-bold">Permission Model</span>
                {'\n\n'}
                User management operations are protected by role-based permissions:
                {'\n'}
                • <span className="font-semibold">company_owner:</span> Full access to all user operations
                {'\n'}
                • <span className="font-semibold">manager:</span> Limited access (view, edit, lock - no delete/changeRole)
                {'\n'}
                • <span className="font-semibold">technician:</span> No user management access
                {'\n'}
                • <span className="font-semibold">operator:</span> No user management access
              </p>
            </div>
          </div>
        );

      case 'demo':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Demo Data Management</h2>

            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <p className="text-sm text-green-900">
                <span className="font-bold">Complete Demo Data Control System</span>
                {'\n\n'}
                Manage demo data pollution with ability to clean and re-seed. Demo users can freely create
                requests, branches, and upload avatars. All changes persist until explicit reset.
              </p>
            </div>

            <ExpandableItem title="Overview" defaultExpanded={true}>
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">What is Demo Data Management?</p>
                  <p>
                    System for controlling demo data pollution. Demo users can freely experiment without data
                    being reset, but admin can cleanly reset to original state with one API call.
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Key Features:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><span className="font-semibold">isDemo Flag:</span> Distinguishes demo data from user-created data</li>
                    <li><span className="font-semibold">Safe Reset:</span> Deletes ONLY user-created data (isDemo=false)</li>
                    <li><span className="font-semibold">Data Persistence:</span> User changes persist across login/logout</li>
                    <li><span className="font-semibold">Multi-Company Safe:</span> Won&apos;t affect production data</li>
                    <li><span className="font-semibold">Re-seeding:</span> Automatic restoration of original demo state</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Tables with isDemo Flag:</p>
                  <ul className="list-disc ml-5 space-y-1 font-mono text-xs">
                    <li>branch - Demo branches (North, South) vs user-created</li>
                    <li>service_request - Demo requests (WM000001-006) vs user-created</li>
                    <li>request_update - Demo updates vs user-created</li>
                  </ul>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="POST /api/demo/reset - Reset Demo Data">
              <div className="space-y-3 text-sm text-gray-700">
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-xs font-semibold text-red-900">🔐 RESTRICTED ACCESS</p>
                  <p className="text-xs text-red-800">Only users with <span className="font-mono bg-red-100 px-1">app_owner</span> role can access this endpoint</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Purpose:</p>
                  <p>Safely reset all demo data to original state, deleting all user-created data. Only accessible to app_owner role for security.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">What Gets Deleted/Reset:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>User-created request updates (isDemo=false)</li>
                    <li>User-created service requests (isDemo=false)</li>
                    <li>User-created branches (isDemo=false)</li>
                    <li>User avatar uploads</li>
                    <li>Default branch assignments (reset to North District for all demo users)</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">What Is Preserved/Restored:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Original demo data (isDemo=true)</li>
                    <li>Demo users (company_owner, manager, technician, operator)</li>
                    <li>Demo company structure</li>
                    <li>Demo branch assignments (re-seeded with proper defaults)</li>
                    <li>All demo users assigned to North District as default branch</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Authorization:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><span className="font-semibold">Required Role:</span> app_owner</li>
                    <li><span className="font-semibold">Denial Response:</span> 403 Forbidden if user lacks required role</li>
                    <li><span className="font-semibold">Unauthenticated:</span> 401 Unauthorized if no auth header</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Safety:</p>
                  <p className="font-mono bg-gray-100 rounded px-2 py-1 text-xs">
                    WHERE isDemo=false AND companyId IN (SELECT id FROM company WHERE isDemo=true)
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Only affects demo company, never production</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Success Response (200):</p>
                  <p className="font-mono text-xs bg-gray-100 rounded p-2">
{`{
  "success": true,
  "message": "Demo data reset and re-seeded successfully",
  "deleted": {
    "requestUpdates": 5,
    "serviceRequests": 2,
    "userBranches": 3,
    "branches": 1,
    "avatars": 4
  },
  "demoCompanies": [
    {
      "id": "83b9a30c-4bc1-4840-b661-93edfc21ab9e",
      "name": "AquaFlow Demo Company"
    }
  ]
}`}
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Error Responses:</p>
                  <p className="text-xs mb-1"><span className="font-semibold">401 Unauthorized:</span></p>
                  <p className="font-mono text-xs bg-gray-100 rounded p-2 mb-2">
{`{"error": "Unauthorized"}`}
                  </p>
                  <p className="text-xs mb-1"><span className="font-semibold">403 Forbidden:</span></p>
                  <p className="font-mono text-xs bg-gray-100 rounded p-2">
{`{"error": "Forbidden: Only app_owner can reset demo data"}`}
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Example Usage:</p>
                  <p className="font-mono text-xs bg-gray-100 rounded p-2">
                    curl -X POST http://localhost:3000/api/demo/reset
                  </p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="Manual Re-Seed Script">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Purpose:</p>
                  <p>Manually re-seed demo data anytime. Useful for development and testing.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Characteristics:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><span className="font-semibold">Idempotent:</span> Safe to run multiple times</li>
                    <li><span className="font-semibold">Non-destructive:</span> Won&apos;t affect user data</li>
                    <li><span className="font-semibold">Smart:</span> Only creates missing demo data</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Usage:</p>
                  <p className="font-mono text-xs bg-gray-100 rounded p-2">
                    cd backend && bun scripts/seed-demo.ts
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">When to Use:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>After manual database modifications</li>
                    <li>During development/testing</li>
                    <li>To ensure all demo data exists</li>
                  </ul>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="Demo User Workflow">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">1. Initial Login</p>
                  <p>
                    Demo user logs in via <span className="font-mono bg-gray-100 rounded px-1">/api/demo-auth/login</span>.
                    Demo user created (isDemo=true), required demo branches assigned.
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">2. Experimentation</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Create new requests (marked isDemo=false)</li>
                    <li>Create new branches (marked isDemo=false)</li>
                    <li>Upload avatars (optional, will be deleted on reset)</li>
                    <li><span className="font-semibold">All changes persist</span> across login/logout</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">3. Reset to Original</p>
                  <p>
                    When ready, admin calls <span className="font-mono bg-gray-100 rounded px-1">POST /api/demo/reset</span>.
                    All user-created data deleted, demo data restored, system re-seeded.
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">4. Fresh Cycle Starts</p>
                  <p>
                    Demo company is clean and back to original seed data. Demo users still exist.
                    Ready for next round of testing.
                  </p>
                </div>
              </div>
            </ExpandableItem>

            <ExpandableItem title="Demo Data Structure">
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold mb-1">Demo Company</p>
                  <ul className="list-disc ml-5 space-y-1 text-xs">
                    <li>Name: &quot;AquaFlow Demo Company&quot;</li>
                    <li>isDemo: true</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Demo Branches</p>
                  <ul className="list-disc ml-5 space-y-1 text-xs">
                    <li>North District - &quot;123 Main St, Downtown&quot; (isDemo: true)</li>
                    <li>South District - &quot;456 Oak Ave, Southside&quot; (isDemo: true)</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Demo Users (All with isDemo: true)</p>
                  <ul className="list-disc ml-5 space-y-1 text-xs">
                    <li><span className="font-semibold">demo-company-owner@aquaflow.app</span>
                      <ul className="list-circle ml-5 mt-1 space-y-0.5 text-xs">
                        <li>Assigned branches: North District, South District</li>
                        <li>Default branch: North District</li>
                        <li>Permissions: Full</li>
                      </ul>
                    </li>
                    <li><span className="font-semibold">demo-manager@aquaflow.app</span>
                      <ul className="list-circle ml-5 mt-1 space-y-0.5 text-xs">
                        <li>Assigned branches: North District</li>
                        <li>Default branch: North District</li>
                        <li>Permissions: Operational</li>
                      </ul>
                    </li>
                    <li><span className="font-semibold">demo-technician@aquaflow.app</span>
                      <ul className="list-circle ml-5 mt-1 space-y-0.5 text-xs">
                        <li>Assigned branches: North District</li>
                        <li>Default branch: North District</li>
                        <li>Permissions: Field service</li>
                      </ul>
                    </li>
                    <li><span className="font-semibold">demo-operator@aquaflow.app</span>
                      <ul className="list-circle ml-5 mt-1 space-y-0.5 text-xs">
                        <li>Assigned branches: North District</li>
                        <li>Default branch: North District</li>
                        <li>Permissions: View-only</li>
                      </ul>
                    </li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Demo Service Requests (All with isDemo: true)</p>
                  <ul className="list-disc ml-5 space-y-1 text-xs">
                    <li>WM000001: Major Pipe Burst on Main Street (urgent)</li>
                    <li>WM000002: Low Water Pressure - Residential Area (in_progress)</li>
                    <li>WM000003: Scheduled Valve Replacement (pending)</li>
                    <li>WM000004: Minor Leak at Fire Hydrant (low priority)</li>
                    <li>WM000005: Water Main Repair Completed (completed)</li>
                    <li>WM000006: Emergency Water Restoration (urgent, in_progress)</li>
                  </ul>
                </div>
              </div>
            </ExpandableItem>

            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 mt-6">
              <p className="text-sm text-yellow-900">
                <span className="font-semibold">Important:</span> Demo data management only affects
                demo companies (where <span className="font-mono">company.isDemo = true</span>). Production
                company data is completely protected and never affected by reset operations.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Documentation:</span> See
                {' '}<span className="font-mono">DEMO_DATA_MANAGEMENT.md</span> for complete implementation details,
                workflows, and testing scenarios.
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Developer Documentation</h2>
            <p className="text-gray-600">Select a tab from the left menu to get started.</p>
          </div>
        );
    }
  };

  return (
    <div className="p-8 max-w-5xl">
      {renderContent()}
    </div>
  );
}
